/**
 * Per-module list/filter UI config for metadata-driven pages.
 * Backend may extend or override via system_modules.module_config (JSON).
 */

const DEFAULT_LIST_CONFIG = {
  lookupContext: {},
};

/**
 * Frontend defaults keyed by system_modules.module_name.
 * lookupContext is merged into FK lookup API calls (FilterService.fetchLookupOptions).
 */
const MODULE_LIST_REGISTRY = {
  employees: {
    lookupContext: { per_page: "all" },
  },
  employee_exits: {
    lookupContext: { per_page: "all" },
  },
  organization_locations: {
    lookupContext: { per_page: "all" },
  },
  departments: {
    lookupContext: { per_page: "all" },
  },
  organization_designations: {
    lookupContext: { per_page: "all" },
  },
  organization_work_shifts: {
    lookupContext: { per_page: "all" },
  },
  organization_users: {
    lookupContext: { per_page: "all" },
  },
  organization_roles: {
    lookupContext: { per_page: "all" },
  },
  organization_project_tasks: {
    lookupContext: { mode: "3" },
  },
  organization_project_task_schedules: {
    lookupContext: { mode: "3" },
  },
  organization_project_task_schedule_time_logs: {
    lookupContext: { mode: "3" },
  },
  organization_project_projects: {
    lookupContext: { mode: "3" },
  },
  organization_project_teams: {
    lookupContext: { mode: "3" },
  },
  organization_project_team_members: {
    lookupContext: { mode: "3" },
  },
  organization_clients: {
    lookupContext: { mode: "3" },
  },
  ems_leads: {
    lookupContext: { per_page: "all" },
  },
  ems_lead_activities: {
    lookupContext: { per_page: "all" },
  },
  ems_lead_contact_timings: {
    lookupContext: { per_page: "all" },
  },
  ems_demo_sessions: {
    lookupContext: { per_page: "all" },
  },
  ems_demo_participants: {
    lookupContext: { per_page: "all" },
  },
  ems_students: {
    lookupContext: { per_page: "all" },
  },
  ems_admissions: {
    lookupContext: { per_page: "all" },
  },
  ems_fee_installments: {
    lookupContext: { per_page: "all" },
  },
  ems_student_fees: {
    lookupContext: { per_page: "all" },
  },
  ems_batches: {
    lookupContext: { per_page: "all" },
  },
  ems_batch_students: {
    lookupContext: { per_page: "all" },
  },
  ems_batch_classes: {
    lookupContext: { per_page: "all" },
  },
  ems_batch_class_records: {
    lookupContext: { per_page: "all" },
  },
  ems_class_attendance: {
    lookupContext: { per_page: "all" },
  },
  ems_assessments: {
    lookupContext: { per_page: "all" },
  },
  ems_assessment_results: {
    lookupContext: { per_page: "all" },
  },
  ems_training_programs: {
    lookupContext: { per_page: "all" },
  },
  ems_training_program_categories: {
    lookupContext: { per_page: "all" },
  },
  ems_certificates: {
    lookupContext: { per_page: "all" },
  },
  ems_recruitment_agencies: {
    lookupContext: { per_page: "all" },
  },
  ems_companies: {
    lookupContext: { per_page: "all" },
  },
  ems_placement_referrals: {
    lookupContext: { per_page: "all" },
  },
  ems_student_review_platforms: {
    lookupContext: { per_page: "all" },
  },
  ems_student_reviews: {
    lookupContext: { per_page: "all" },
  },
  ems_lead_stages: {
    lookupContext: { per_page: "all" },
  },
  ems_lead_sources: {
    lookupContext: { per_page: "all" },
  },
  ems_training_spaces: {
    lookupContext: { per_page: "all" },
  },
};

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function mergeListConfig(base, patch) {
  if (!isPlainObject(patch)) {
    return base;
  }

  const merged = { ...base };

  if (isPlainObject(patch.lookupContext)) {
    merged.lookupContext = {
      ...(base.lookupContext || {}),
      ...patch.lookupContext,
    };
  }

  Object.entries(patch).forEach(([key, value]) => {
    if (key === "lookupContext") return;
    merged[key] = value;
  });

  return merged;
}

/**
 * @param {string} moduleName - system_modules.module_name
 * @param {string|null} _routePath - reserved for route-based overrides
 * @param {object|null} apiModuleConfig - system_modules.module_config from GET /api/metadata/{module}
 */
export function resolveModuleListConfig(
  moduleName,
  _routePath = null,
  apiModuleConfig = null
) {
  const registryConfig = MODULE_LIST_REGISTRY[moduleName] || {};
  let resolved = mergeListConfig(DEFAULT_LIST_CONFIG, registryConfig);

  if (isPlainObject(apiModuleConfig)) {
    if (isPlainObject(apiModuleConfig.list_config)) {
      resolved = mergeListConfig(resolved, apiModuleConfig.list_config);
    } else {
      resolved = mergeListConfig(resolved, apiModuleConfig);
    }
  }

  return resolved;
}

export default {
  resolveModuleListConfig,
  MODULE_LIST_REGISTRY,
};
